import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { useCart } from "../../../contexts/CartContext";
import { useNotification } from "../../../contexts/NotificationContext";
import { useConfirmation } from "../../../contexts/ConfirmationContext";
import { isAdminEmail } from "../../../constants/admin";
import { Search, ShoppingCart, Menu, X, User } from "lucide-react";
import CartDrawer from "../CartDrawer/CartDrawer";

const Navbar = () => {
  const { currentUser, logout } = useAuth();
  const { getTotalItems } = useCart();
  const { showSuccess, showError } = useNotification();
  const { showConfirmation } = useConfirmation();
  const navigate = useNavigate();
  const location = useLocation();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [showEmptyTooltip, setShowEmptyTooltip] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const handleLogout = async () => {
    const confirmed = await showConfirmation({
      title: "Logout",
      message: "Are you sure you want to logout? You will need to sign in again to access your account.",
      confirmText: "Yes, Logout",
      cancelText: "Stay Logged In",
      type: "logout",
    });
    if (!confirmed) return;
    try {
      await logout();
      showSuccess("Logged out successfully!");
      navigate("/");
    } catch (error) {
      console.error("Failed to log out:", error);
      showError("Failed to logout. Please try again.");
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?q=${encodeURIComponent(searchQuery.trim())}`);
      setIsSearchOpen(false);
      setIsMenuOpen(false);
    }
  };

  const isAdmin = currentUser?.email && isAdminEmail(currentUser.email);
  const isActive = (path) => location.pathname === path;

  const userLinks = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/products", label: "Products" },
    { to: "/contact", label: "Contact Us" },
  ];
  const adminLinks = [
    { to: "/admin", label: "Dashboard" },
    { to: "/admin/add-product", label: "Products" },
    { to: "/admin/orders", label: "Orders" },
    { to: "/admin/reviews", label: "Reviews" },
  ];
  const links = isAdmin ? adminLinks : userLinks;

  const linkClasses = (path) =>
    `px-3 lg:px-4 py-2 rounded-lg transition-colors duration-300 font-semibold text-sm lg:text-base ${
      isActive(path)
        ? "bg-[#DDBB92] text-[#2B2A29]"
        : "text-gray-300 hover:bg-[#E3D2BD] hover:text-[#2B2A29]"
    }`;

  return (
    <div className="bg-[#484139] px-2 sm:px-4 pt-2 sm:pt-4">
      <nav className="bg-[#0D0806] text-white relative rounded-lg overflow-visible">
        <div className="container mx-auto px-4 sm:px-6 h-16 md:h-20 flex items-center justify-between gap-3">
          {/* Brand / Logo */}
          <Link
            to={isAdmin ? "/admin" : "/"}
            className="flex items-center md:absolute md:left-1/2 md:-translate-x-1/2 z-10"
          >
            <img
              src="/Logo.png"
              alt="Glowy Logo"
              className="h-12 sm:h-16 md:h-20 lg:h-24 object-contain"
            />
          </Link>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-1 lg:gap-2">
            {links.map((l) => (
              <Link key={l.to} to={l.to} className={linkClasses(l.to)}>
                {l.label}
              </Link>
            ))}
          </div>

          {/* Desktop right-side actions */}
          <div className="hidden md:flex items-center gap-3 lg:gap-4">
            {!isAdmin && (
              <>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsSearchOpen(!isSearchOpen)}
                    className="bg-[#DDBB92] text-[#2B2A29] p-2.5 rounded-lg hover:opacity-90 transition"
                    aria-label="Search"
                  >
                    <Search className="w-5 h-5" />
                  </button>
                  {isSearchOpen && (
                    <form
                      onSubmit={handleSearch}
                      className="absolute right-0 mt-3 bg-white text-black px-3 py-2 rounded-lg shadow-xl flex items-center gap-2 w-64 z-50"
                    >
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search products..."
                        className="flex-1 border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#DDBB92]"
                        autoFocus
                      />
                      <button
                        type="submit"
                        className="bg-[#DDBB92] text-[#2B2A29] px-3 py-1 rounded-md font-semibold hover:bg-[#e6c79b] transition"
                      >
                        Go
                      </button>
                    </form>
                  )}
                </div>

                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      if (getTotalItems() > 0) setIsCartOpen(true);
                    }}
                    onMouseEnter={() => setShowEmptyTooltip(getTotalItems() === 0)}
                    onMouseLeave={() => setShowEmptyTooltip(false)}
                    className="relative bg-[#DDBB92] text-[#2B2A29] p-2.5 rounded-lg hover:opacity-90 transition"
                    aria-label="Open cart"
                  >
                    <ShoppingCart className="w-5 h-5" />
                    {currentUser && getTotalItems() > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                        {getTotalItems()}
                      </span>
                    )}
                  </button>
                  {showEmptyTooltip && (
                    <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 bg-yellow-100 text-[#2B2A29] text-xs px-3 py-1.5 rounded-lg shadow-lg whitespace-nowrap border border-yellow-300 animate-fade-in z-50">
                      Cart is empty
                    </div>
                  )}
                </div>
              </>
            )}

            {currentUser ? (
              <div className="relative group">
                <button className="flex items-center gap-2 text-white hover:text-beige-300 transition">
                  <User className="w-5 h-5" />
                  <span className="text-sm">
                    {currentUser.displayName || currentUser.email.split("@")[0]}
                  </span>
                </button>
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                  <Link to="/my-orders" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    My Orders
                  </Link>
                  <Link to="/my-profile" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    My Profile
                  </Link>
                  {isAdmin && (
                    <Link to="/admin" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Admin Panel
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Logout
                  </button>
                </div>
              </div>
            ) : (
              <Link
                to="/login"
                className="bg-[#DDBB92] text-[#2B2A29] px-4 lg:px-6 py-2 rounded-lg font-semibold hover:opacity-90 transition flex items-center gap-2 text-sm lg:text-base"
              >
                Login/Register
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-white p-2 rounded-lg hover:bg-[#1f1916] transition ml-auto"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-brown-700 px-4 py-4 animate-fade-in">
            {!isAdmin && (
              <form onSubmit={handleSearch} className="mb-4 flex gap-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search products..."
                  className="flex-1 bg-white text-gray-800 placeholder-gray-500 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#DDBB92]"
                />
                <button
                  type="submit"
                  className="bg-[#DDBB92] text-[#2B2A29] px-4 rounded-lg font-semibold hover:opacity-90 transition"
                >
                  <Search className="w-5 h-5" />
                </button>
              </form>
            )}

            <div className="flex flex-col gap-1">
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block px-3 py-2 rounded-lg font-medium ${
                    isActive(l.to)
                      ? "bg-[#DDBB92] text-[#2B2A29]"
                      : "text-gray-300 hover:bg-[#1f1916]"
                  }`}
                >
                  {l.label}
                </Link>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-[#3a322c] space-y-2">
              {!isAdmin && currentUser && (
                <button
                  onClick={() => {
                    if (getTotalItems() > 0) setIsCartOpen(true);
                    setIsMenuOpen(false);
                  }}
                  className="w-full flex items-center justify-between bg-[#DDBB92] text-[#2B2A29] px-3 py-2 rounded-lg font-semibold"
                >
                  <span className="flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5" />
                    Cart
                  </span>
                  <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                    {getTotalItems()}
                  </span>
                </button>
              )}
              {currentUser ? (
                <>
                  <Link
                    to="/my-orders"
                    onClick={() => setIsMenuOpen(false)}
                    className="block px-3 py-2 rounded-lg text-gray-300 hover:bg-[#1f1916]"
                  >
                    My Orders
                  </Link>
                  <Link
                    to="/my-profile"
                    onClick={() => setIsMenuOpen(false)}
                    className="block px-3 py-2 rounded-lg text-gray-300 hover:bg-[#1f1916]"
                  >
                    My Profile
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    className="w-full text-left px-3 py-2 rounded-lg text-gray-300 hover:bg-[#1f1916]"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setIsMenuOpen(false)}
                  className="block w-full text-center bg-[#DDBB92] text-[#2B2A29] px-4 py-2 rounded-lg font-semibold hover:opacity-90 transition"
                >
                  Login/Register
                </Link>
              )}
            </div>
          </div>
        )}
      </nav>

      <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </div>
  );
};

export default Navbar;
